import {
  createRef,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { FilterSchema } from '@adc/blueprint'
import { debounce } from 'lodash'
import ms from 'ms'
import useBreakpoints from '../useBreakpoints'

const BUTTON_SAFETY_GAP = 12 // We will measure button sizes here so adding some safety gaps for the separation added
const DEFAULT_MAX_BUTTON_WIDTH = 240
const DEFAULT_MIN_BUTTON_WIDTH = 210 // We need to force enough space for each button to grow

interface UseListingFilterButtonsProps {
  data: FilterSchema.FilterGroupViewModelCollection
  definitions: FilterSchema.FilterGroupDefinitionArray
  maxButtonWidth?: number
  minButtonWidth?: number
}

const useFilterButtons = ({
  data,
  definitions,
  maxButtonWidth = DEFAULT_MAX_BUTTON_WIDTH,
  minButtonWidth = DEFAULT_MIN_BUTTON_WIDTH,
}: UseListingFilterButtonsProps) => {
  const breakpoint = useBreakpoints()

  const containerRef = useRef<HTMLDivElement>(null)
  const filterButtonRefs = useRef<
    Record<string, React.RefObject<HTMLButtonElement>>
  >({})
  const visibleButtonsRef = useRef<FilterSchema.FilterGroupDefinitionArray>([])
  const extraButtonsRef = useRef<HTMLDivElement | null>(null) // To pass any extra button supposed to be part of the row that is NOT a filter. IE: 'Clear Filters'

  const [visibleItems, setVisibleItems] =
    useState<FilterSchema.FilterGroupDefinitionArray>([])

  const getButtonsSizes = useCallback(() => {
    let totalNonHideableWidth = 0
    let totalNonHideableElements = 0
    const buttonsWithSizes: Record<
      string,
      FilterSchema.FilterGroupDefinition & { width: number }
    > = {}

    const currentList = visibleButtonsRef.current.length
      ? visibleButtonsRef.current
      : definitions

    currentList.forEach(item => {
      const buttonRefWidth =
        filterButtonRefs?.current?.[item.name]?.current?.offsetWidth
      const buttonWidth =
        (buttonRefWidth ? buttonRefWidth : minButtonWidth) + BUTTON_SAFETY_GAP

      if (item.mode === 'completeFilter') {
        totalNonHideableWidth += buttonWidth + BUTTON_SAFETY_GAP
        totalNonHideableElements++
      }

      buttonsWithSizes[item.name] = { ...item, width: buttonWidth }
    })

    const containerWidth = containerRef.current?.offsetWidth || 0
    const extraButtonsWidth = extraButtonsRef.current?.offsetWidth || 0
    const availableSpace =
      containerWidth -
      totalNonHideableWidth -
      (extraButtonsWidth + BUTTON_SAFETY_GAP)

    return {
      availableSpace,
      buttonsWithSizes,
      totalNonHideableElements,
      totalNonHideableWidth,
    }
  }, [minButtonWidth])

  const setButtonRefMaxWidth = (name: string, width = maxButtonWidth) => {
    filterButtonRefs.current[name]?.current?.style.setProperty(
      '--button-max-width',
      `${width}px`
    )
  }

  const updateVisibleItems = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const visibleMap: Record<string, FilterSchema.FilterGroupDefinition> = {}

    if (breakpoint === 'mobile') {
      definitions.forEach(def => {
        if (def.mode === 'completeFilter') visibleMap[def.name] = def
      })
    } else {
      const { buttonsWithSizes, availableSpace } = getButtonsSizes()
      let totalWidth = 0

      definitions.forEach(item => {
        const isHideable = item.mode !== 'completeFilter'
        const itemWidth = buttonsWithSizes[item.name]?.width

        if (itemWidth) {
          // Item is rendered, if it doesn't fit, do not add it again to visible items.
          totalWidth += Math.max(minButtonWidth + BUTTON_SAFETY_GAP, itemWidth)
          const fits = totalWidth < availableSpace
          const shouldShow = !isHideable || fits
          if (shouldShow) visibleMap[item.name] = item
        } else if (totalWidth + maxButtonWidth < availableSpace) {
          // If item is not rendered, use the max possible size it can have to make sure it fits before adding it back.
          totalWidth += maxButtonWidth
          visibleMap[item.name] = item
        }
      })
    }
    // We need to remove non-visible buttons refs and add the new ones, only if they changed
    definitions.forEach(def => {
      const isVisible = !!visibleMap[def.name]
      if (isVisible && !filterButtonRefs.current[def.name]) {
        filterButtonRefs.current[def.name] = createRef<HTMLButtonElement>()
      } else if (!isVisible && filterButtonRefs.current[def.name]) {
        delete filterButtonRefs.current[def.name]
      } else if (isVisible) {
        setButtonRefMaxWidth(def.name)
      }
    })

    const visibleArray = Object.values(visibleMap)
    visibleButtonsRef.current = visibleArray
    setVisibleItems(visibleArray)
  }, [breakpoint, getButtonsSizes, minButtonWidth, maxButtonWidth])

  const debouncedUpdateVisibleItems = useMemo(
    () => debounce(updateVisibleItems, ms('100ms')),
    [updateVisibleItems]
  )

  useEffect(() => {
    const observer = new ResizeObserver(debouncedUpdateVisibleItems)
    if (containerRef.current) observer.observe(containerRef.current)

    return () => {
      observer.disconnect()
      debouncedUpdateVisibleItems.cancel()
    }
  }, [debouncedUpdateVisibleItems])

  useEffect(() => {
    if (breakpoint === 'mobile') return

    const { availableSpace, totalNonHideableElements } = getButtonsSizes()
    const visibleCount = visibleButtonsRef.current.length

    if (visibleCount > totalNonHideableElements) {
      const hideableCount = visibleCount - totalNonHideableElements
      const buttonGapsWidth = (hideableCount - 1) * BUTTON_SAFETY_GAP
      const newMaxWidth =
        (availableSpace - buttonGapsWidth + BUTTON_SAFETY_GAP) / hideableCount

      if (newMaxWidth < maxButtonWidth) {
        visibleButtonsRef.current.forEach(btn =>
          setButtonRefMaxWidth(btn.name, newMaxWidth)
        )
      }
    }
  }, [data, getButtonsSizes])

  return { containerRef, extraButtonsRef, filterButtonRefs, visibleItems }
}

export default useFilterButtons
